<template>
  <b-row class="resource-list-header mb-2">
    <b-col sm="12" md="6">
      <div class="d-flex justify-content-start align-items-center">
        <b-form-input
          v-if="!hideSearch"
          v-model="searchInput"
          placeholder="Search..."
          class="search-input"
          @input="onSearchInput"
        />
      </div>
    </b-col>
    <b-col sm="12" md="6">
      <div class="d-flex justify-content-end align-items-center">
        <b-button
          v-if="!hideBatch"
          variant="outline-secondary"
          class="mr-1"
          @click="$emit('batch')"
          :disabled="loadingBatch"
        >
          <feather-icon v-if="!loadingBatch" icon="LayersIcon" class="mr-50" />
          <b-spinner v-else small class="mr-50"></b-spinner>
          <span>Batch</span>
        </b-button>
        <!-- Watch Now button (before Export) -->
        <b-button
          v-if="watchLink && !hideWatch"
          variant="outline-success"
          class="mr-1"
          @click="openWatch"
        >
          <feather-icon icon="PlayIcon" class="mr-50" />
          <span>{{ watchLabel }}</span>
        </b-button>
        <b-button
          v-if="!hideExport"
          variant="outline-primary"
          class="mr-1"
          @click="$emit('export')"
          :disabled="isExporting"
        >
          <b-spinner v-if="isExporting" small class="mr-50"></b-spinner>
          <feather-icon v-else icon="DownloadIcon" class="mr-50" />
          <span>{{ isExporting ? 'Exporting...' : 'Export' }}</span>
        </b-button>
        <b-button
          v-if="!hideImport"
          variant="outline-info"
          class="mr-1"
          @click="$emit('import')"
          :disabled="isImporting"
        >
          <b-spinner v-if="isImporting" small class="mr-50"></b-spinner>
          <feather-icon v-else icon="UploadIcon" class="mr-50" />
          <span>{{ isImporting ? 'Importing...' : 'Import' }}</span>
        </b-button>
        <b-button v-if="!hideAdd && canAdd" variant="primary" @click="$emit('add')">
          <feather-icon icon="PlusIcon" class="mr-50" />
          <span>New {{ resourceSingular }}</span>
        </b-button>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import { RESOURCES } from '@/utils/constants';
import { computedResourceSingular } from '@/utils/functions';
import { BButton, BCol, BFormInput, BRow, BSpinner } from 'bootstrap-vue';
import { mapState } from 'vuex';

export default {
  name: 'ResourceListHeader',
  components: {
    BRow,
    BCol,
    BButton,
    BFormInput,
    BSpinner,
  },
  props: {
    resource: {
      type: [String, Object],
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    hideSearch: {
      type: Boolean,
      default: false,
    },
    hideAdd: {
      type: Boolean,
      default: false,
    },
    hideExport: {
      type: Boolean,
      default: false,
    },
    hideBatch: {
      type: Boolean,
      default: true,
    },
    hideImport: {
      type: Boolean,
      default: true,
    },
    isExporting: {
      type: Boolean,
      default: false,
    },
    isImporting: {
      type: Boolean,
      default: false,
    },
    loadingBatch: {
      type: Boolean,
      default: false,
    },
    // New props for Watch Now button
    watchLink: {
      type: String,
      default: '',
    },
    hideWatch: {
      type: Boolean,
      default: false,
    },
    watchLabel: {
      type: String,
      default: 'Watch Now',
    },
  },
  data() {
    return {
      searchInput: this.value,
      searchTimeout: null,
    };
  },
  watch: {
    value(newVal) {
      this.searchInput = newVal;
    },
  },
  computed: {
    ...mapState('user', ['authRoutes']),

    resourceObj() {
      if (typeof this.resource === 'string') {
        return Object.values(RESOURCES).find((res) => res.name === this.resource) || {};
      }
      return this.resource;
    },

    resourceLabel() {
      return this.resourceObj.label ?? this.resourceObj.name;
    },

    resourceRouteName() {
      // Get the route name based on different possibilities
      if (this.resourceObj.route) {
        return this.resourceObj.route;
      }
      if (this.resourceObj.routeName) {
        return this.resourceObj.routeName;
      }
      return '';
    },

    routePermissions() {
      const routeName = this.resourceRouteName;

      // Check if this resource has a direct add property
      if (this.resourceObj.add !== undefined) {
        return this.resourceObj;
      }

      // Check permissions from authRoutes
      if (routeName && this.authRoutes && this.authRoutes[routeName]) {
        return this.authRoutes[routeName];
      }

      // Default permissions
      return { view: true, add: true, edit: true, delete: true };
    },

    canAdd() {
      return this.routePermissions.add === true;
    },

    resourceSingular() {
      return computedResourceSingular(this.resourceObj);
    },
  },
  methods: {
    onSearchInput() {
      // Debounce search input
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        console.log('=== searchInput', this.searchInput);
        this.$emit('search', this.searchInput);
        this.$emit('input', this.searchInput);
      }, 500);
    },
    openWatch() {
      try {
        if (this.watchLink) {
          window.open(this.watchLink, '_blank', 'noopener');
        }
      } catch (e) {
        // Fallback: emit so parent can handle if needed
        this.$emit('watch', this.watchLink);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.resource-list-header {
  .search-input {
    max-width: 250px;
  }
}
</style>
